import { useCallback, useState } from 'react'

export interface InputBinder {
  value: string
  onChange: (value: string) => void
}

interface Output {
  value: string
  reset: (value?: string) => void
  binder: InputBinder
}

export function useInput(initial: string = ''): Output {
  const [value, setValue] = useState<string>(initial)

  const reset = (value?: string): void => setValue(value ?? initial)
  const onChange = (value: string): void => setValue(value)

  return {
    value,
    reset,
    binder: {
      value,
      onChange,
    },
  }
}

function formatTimeInput(input: string): string {
  if (!input) return ''

  const digits = input.replace(/\D/g, '')

  if (digits.length === 0) return ''
  if (digits.length === 1) return digits
  if (digits.length === 2) return digits
  if (digits.length === 3) return `${digits.slice(0, 2)}:${digits.slice(2, 3)}`
  if (digits.length >= 4) {
    const hours = Math.min(23, parseInt(digits.slice(0, 2), 10) || 0)
    const minutes = Math.min(59, parseInt(digits.slice(2, 4), 10) || 0)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  }

  return digits
}

export function useTimeInput(initial: string = ''): Output {
  const [value, setValue] = useState<string>(initial)

  const reset = useCallback(
    (newValue?: string): void => {
      setValue(newValue ?? initial)
    },
    [initial],
  )

  const onChange = useCallback((input: string): void => {
    const formatted = formatTimeInput(input)
    setValue(formatted)
  }, [])

  return {
    value,
    reset,
    binder: {
      value,
      onChange,
    },
  }
}
